import React, { forwardRef } from 'react'
import { cn } from '../../lib/cn'

export type CircleButtonKind = 'default' | 'light' | 'ghost'

export interface CircleButtonProps
  extends React.ComponentPropsWithRef<'button'> {
  icon: React.ReactNode
  kind?: CircleButtonKind
  'aria-label': string
  /**
   * Optional text label rendered to the right of the icon. Turns the 32px
   * circle into a pill (same height, auto width) — the labeled variant of the
   * floating icon button.
   */
  label?: string
  /**
   * Render the styles onto a different element (e.g. `<Link />`) instead of a
   * `<button>` — same pattern as the app-wide Ariakit `render` prop
   * convention. The element receives the computed className, the icon/label
   * children, and the remaining props. Use for navigation so links keep real
   * anchor semantics (no button-inside-link nesting).
   */
  render?: React.ReactElement<{
    className?: string
    children?: React.ReactNode
  }>
  /**
   * Grow the pointer/tap target to 48px without growing the 32px circle, via a
   * centred transparent overlay. Padding cannot do this: the fill and the
   * rounding are on the button box, so padding scales the visible circle with
   * the target. Use wherever the control sits in a touch surface and the 32px
   * visual has to stay.
   */
  expandHitArea?: boolean
}

const kindClasses: Record<CircleButtonKind, string> = {
  // Dark (Figma "Dark" theme): #2f2f2f fill, lightens to #3e3e3e on hover,
  // white icon. Uses the button-secondary-* tokens.
  default:
    'bg-edu-button-secondary-bg-default text-edu-fg-primary hover:bg-edu-button-secondary-bg-hover',
  // Light (Figma "Light" theme — used on the subject modal): white fill that
  // dims to #cecece on hover, dark icon. Uses the button-primary-* tokens.
  light:
    'bg-edu-button-primary-bg-default text-edu-button-text hover:bg-edu-button-primary-bg-hover',
  // Ghost: transparent at rest; hover fills with the same token as the
  // default kind's hover (#3e3e3e) so all CircleButton hovers read alike.
  ghost:
    'bg-transparent text-edu-fg-primary hover:bg-edu-button-secondary-bg-hover active:bg-edu-bg-tertiary',
}

export const CircleButton = forwardRef<HTMLButtonElement, CircleButtonProps>(
  (
    {
      icon,
      kind = 'default',
      label,
      render,
      className,
      disabled,
      expandHitArea = false,
      ...props
    },
    ref,
  ) => {
    const classes = cn(
      'flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-out',
      // Circle when icon-only; pill (auto width, 8px gap, 12px side padding)
      // when labeled.
      label ? 'gap-2 px-3' : 'w-8',
      kindClasses[kind],
      expandHitArea &&
        "relative after:absolute after:left-1/2 after:top-1/2 after:size-12 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
      disabled && 'pointer-events-none opacity-30',
      className,
    )

    const content = (
      <>
        {icon}
        {label != null && (
          // body-5 composite (11px caption style) on its own element, so it
          // never shares a tailwind-merge text-* group with the kind colors.
          <span className='whitespace-nowrap text-body-5'>{label}</span>
        )}
      </>
    )

    if (render) {
      return React.cloneElement(render, {
        ...props,
        // Non-button elements have no native `disabled`: pointer-events-none
        // (in `classes`) blocks the mouse, but a focused link would still
        // activate on Enter — so also drop it from the tab order, announce
        // the state, and swallow activation.
        ...(disabled
          ? {
              'aria-disabled': true,
              tabIndex: -1,
              onClick: (e: React.MouseEvent) => e.preventDefault(),
            }
          : {}),
        ref,
        className: cn(classes, render.props.className),
        children: content,
      } as React.HTMLAttributes<HTMLElement>)
    }

    return (
      <button
        ref={ref}
        type='button'
        disabled={disabled ?? false}
        className={classes}
        {...props}
      >
        {content}
      </button>
    )
  },
)

CircleButton.displayName = 'CircleButton'
